import { useState, useTransition } from "react";
import { useRevalidate } from "@/lib/loader";
import type {
  PendingWorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
} from "@/lib/api/types";
import { inviteMember, removeMember } from "./membersActions";

const ROLE_OPTIONS: { value: WorkspaceRole; label: string }[] = [
  { value: "OWNER", label: "Owner" },
  { value: "ADMIN", label: "Admin" },
  { value: "MEMBER", label: "Member" },
];

function roleLabel(role: WorkspaceRole): string {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;
}

export function MembersTable({
  members,
  invitations,
  canManage,
  currentEmail,
  myRole,
}: {
  members: WorkspaceMember[];
  invitations: PendingWorkspaceInvitation[];
  canManage: boolean;
  currentEmail: string;
  myRole: WorkspaceRole;
}) {
  const revalidate = useRevalidate();
  const [pending, startTransition] = useTransition();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("MEMBER");
  const [error, setError] = useState<string | null>(null);

  const ownerCount = members.filter((m) => m.role === "OWNER").length;

  function run(action: () => Promise<{ ok: boolean; error?: string }>) {
    setError(null);
    startTransition(async () => {
      const res = await action();
      if (!res.ok) setError(res.error ?? "Action failed");
      else revalidate();
    });
  }

  return (
    <div className="space-y-4">
      {canManage && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              const res = await inviteMember(email, role);
              if (res.ok) setEmail("");
              return res;
            });
          }}
          className="flex flex-wrap items-end gap-2 rounded border border-zinc-200 dark:border-border bg-white dark:bg-surface p-3"
        >
          <label className="flex flex-col text-xs text-zinc-600 dark:text-zinc-400">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@company.com"
              className="mt-1 w-64 rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 dark:border-border dark:bg-surface dark:text-zinc-100"
            />
          </label>
          <label className="flex flex-col text-xs text-zinc-600 dark:text-zinc-400">
            Role
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as WorkspaceRole)}
              className="mt-1 rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 dark:border-border dark:bg-surface dark:text-zinc-100"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={pending}
            className="rounded bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            Invite
          </button>
          {error && (
            <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
          )}
        </form>
      )}

      {!canManage && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          You are a {roleLabel(myRole).toLowerCase()} in this workspace. Only
          owners and admins can invite or remove people.
        </p>
      )}

      <div className="overflow-hidden rounded border border-zinc-200 dark:border-border bg-white dark:bg-surface">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-zinc-100 dark:bg-surface-2 text-xs uppercase text-zinc-600 dark:text-zinc-400">
            <tr>
              <th className="px-4 py-2 font-semibold">Email</th>
              <th className="px-4 py-2 font-semibold">Role</th>
              {canManage && (
                <th className="px-4 py-2 text-right font-semibold">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr>
                <td
                  colSpan={canManage ? 3 : 2}
                  className="px-4 py-6 text-center text-zinc-500 dark:text-zinc-400"
                >
                  No members.
                </td>
              </tr>
            )}
            {members.map((m) => {
              const isSelf = m.email.toLowerCase() === currentEmail;
              // Don't offer removing the last remaining owner (the backend also
              // rejects this; this just hides a button that would always fail).
              const isLastOwner = m.role === "OWNER" && ownerCount <= 1;
              return (
                <tr
                  key={m.userId}
                  className="border-t border-zinc-100 dark:border-border"
                >
                  <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">
                    {m.email}
                    {isSelf && (
                      <span className="ml-2 text-[11px] font-normal text-zinc-400 dark:text-zinc-500">
                        (you)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                    {roleLabel(m.role)}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      {isLastOwner ? (
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">
                          Last owner
                        </span>
                      ) : (
                        <button
                          type="button"
                          disabled={pending}
                          onClick={() =>
                            run(() => removeMember(m.userId))
                          }
                          className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {invitations.length > 0 && (
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Pending invitations
          </h2>
          <div className="overflow-hidden rounded border border-zinc-200 dark:border-border bg-white dark:bg-surface">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-zinc-100 dark:bg-surface-2 text-xs uppercase text-zinc-600 dark:text-zinc-400">
                <tr>
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-4 py-2 font-semibold">Role</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr
                    key={inv.email}
                    className="border-t border-zinc-100 dark:border-border"
                  >
                    <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">
                      {inv.email}
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                      {roleLabel(inv.workspaceRole)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                        Pending
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
